/**
 * Sortable list wrapper for knowledge base drag-and-drop.
 * Provides DndContext, SortableContext, sensors (Pointer + Keyboard), and handleDragEnd.
 * Use with SortableCategoryRow or SortableArticleRow as children.
 */

import React, { useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

export interface SortableKbListProps {
  /** Item ids in current order (used for sortable context and reorder logic) */
  itemIds: string[];
  /** Called when drag ends with new ordered ids. If undefined, dragging is disabled. */
  onReorder?: (orderedIds: string[]) => void;
  children: React.ReactNode;
}

export const SortableKbList: React.FC<SortableKbListProps> = ({
  itemIds,
  onReorder,
  children,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!onReorder) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = itemIds.indexOf(active.id as string);
      const newIndex = itemIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove([...itemIds], oldIndex, newIndex);
      onReorder(reordered);
    },
    [itemIds, onReorder]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={itemIds}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
};
